import type { EventSummary } from '../api/generated/EventSummary'
import { countdown, eventDates } from '../organizer/overview'
import { localDate } from '../planning/grid'

/** The event's first and last days, when it has any. */
export function summaryDays(event: EventSummary): string[] {
  return [event.first_day, event.last_day].filter((day) => day !== null)
}

/** Over when its last day is behind, in its own time zone; an event without days never is. */
export function isPast(event: EventSummary, now: Date): boolean {
  return event.last_day !== null && event.last_day < localDate(now, event.timezone)
}

/**
 * Events not over yet, soonest first and those without days last, then past events, latest
 * first; the API order otherwise.
 */
export function splitEvents(
  events: EventSummary[],
  now: Date,
): { upcoming: EventSummary[]; past: EventSummary[] } {
  const upcoming = events.filter((event) => !isPast(event, now))
  const past = events.filter((event) => isPast(event, now))
  upcoming.sort((a, b) => {
    if (a.first_day === b.first_day) return 0
    if (a.first_day === null) return 1
    if (b.first_day === null) return -1
    return a.first_day.localeCompare(b.first_day)
  })
  past.sort((a, b) => (b.last_day ?? '').localeCompare(a.last_day ?? ''))
  return { upcoming, past }
}

/** `J-12 · 6–7 févr. 2027`, or undefined for an event without days. */
export function eventWhen(event: EventSummary, now: Date): string | undefined {
  const list = summaryDays(event)
  const parts = [countdown(list, localDate(now, event.timezone)), eventDates(list)]
  return parts.filter(Boolean).join(' · ') || undefined
}

/** Share of the places taken, rounded down; undefined without any place to take. */
export function fillPercent({
  filled_places,
  required_places,
}: NonNullable<EventSummary['figures']>) {
  return required_places > 0 ? Math.floor((100 * filled_places) / required_places) : undefined
}
